import { useMutation, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';

/**
 * Edit a topic already on the editorial programme.
 *
 * 🔴 WHY THIS EXISTS. Founder, 2026-09-07: _"I see that there are proposed
 * articles there, but I don't really know how I edit those. Is there somewhere
 * to even edit those manually myself, or do I have to export it?"_ The honest
 * answer was no: `useCreateEditorialTopic` shipped the same day with no update
 * sibling, so a topic could be added and written about but never corrected. A
 * typo in a headline sent to a real client's stakeholder was permanent.
 *
 * It is the exact mirror of the create hook, and the three non-obvious things
 * carry over unchanged:
 *
 * 1. `user_id` COMES FROM `resolveOwnerId()`. `project_tasks` is `account`-scoped
 *    (`table-scope.generated.ts`), so the filter has to be the ACCOUNT OWNER's
 *    id, not the session user's — a teammate filtering on their own auth id
 *    matches zero rows and the update silently succeeds against nothing. That
 *    failure is invisible to the test suite, because vitest mocks Supabase and
 *    never evaluates an RLS policy.
 *
 * 2. THE PROJECT IS NOT CHANGED. An edit revises a topic in place; re-filing it
 *    into another project is a different act, and doing it silently would move
 *    a row out from under the map that is reading it.
 *
 * 3. `pillar_id` IS A TEXT KEY INTO JSONB, not an FK. Pass an id read from the
 *    live taxonomy, never a name — see `reconcile-pillars.ts` for why the ids
 *    are only stable at all.
 *
 * ── 🔴 AND ONE THING THE CREATE HOOK DID NOT HAVE TO THINK ABOUT ────────────
 *
 * `approval_items.snapshot` IS FROZEN AT SEND TIME, BY DESIGN. Editing a topic
 * that has already gone to a client changes the live row while the client keeps
 * seeing — and keeps approving against — the text they were sent. That is
 * correct behaviour (a verdict must attach to the thing the person actually
 * read; `item_snapshot_hash` is NOT NULL for that reason), and it means an edit
 * can silently put the two out of step.
 *
 * So this hook does not hide it: it returns `wasSent`, and the caller is
 * expected to surface "changed since you sent it". Shipping the edit without
 * that marker would be shipping a trap.
 */

export interface EditorialTopicPatch {
  taskId: string;
  topic?: string;
  pillarId?: string;
  branchId?: string | null;
  /** `project_tasks.rationale` — who this is for. */
  audience?: string | null;
  /** `project_tasks.summary` — where the idea came from. */
  reference?: string | null;
}

export interface EditorialTopicUpdateResult {
  taskId: string;
  /**
   * True when this topic is already on a live approval batch, so the client is
   * looking at a frozen snapshot that no longer matches the row.
   */
  wasSent: boolean;
}

export function useUpdateEditorialTopic(workspaceId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: EditorialTopicPatch): Promise<EditorialTopicUpdateResult> => {
      if (!workspaceId) throw new Error('No workspace is selected.');
      if (!input.taskId) throw new Error('No topic was given to edit.');

      const ownerId = await resolveOwnerId();

      /*
       * Tenancy is asserted through the parent project BEFORE the write, the
       * same shape `applyDecisionWriteBack` uses server-side: `project_tasks`
       * carries `user_id` but no workspace column, so "is this row in the
       * workspace I am editing" can only be answered through `projects`.
       * Without it, a task id from another of this owner's 34 workspaces would
       * update happily.
       */
      const { data: task, error: taskError } = await supabase
        .from('project_tasks')
        .select('id, project_id')
        .eq('id', input.taskId)
        .eq('user_id', ownerId)
        .maybeSingle();
      if (taskError) throw new Error(taskError.message);
      if (!task) throw new Error('That topic is not on this account.');

      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('id')
        .eq('id', task.project_id)
        .eq('brand_kit_id', workspaceId)
        .eq('user_id', ownerId)
        .maybeSingle();
      if (projectError) throw new Error(projectError.message);
      if (!project) throw new Error('That topic belongs to a different workspace.');

      /*
       * Only what the caller actually passed. Spreading the whole patch would
       * write `undefined` over a column the editor never showed — an edit form
       * with three fields must not blank a fourth.
       *
       * Typed against the real columns rather than `Record<string, unknown>`:
       * the generated Supabase types reject an untyped object here, and that
       * rejection is worth keeping — it is what catches a renamed column at
       * compile time instead of at runtime against a real client's data.
       */
      const patch: {
        topic?: string;
        pillar_id?: string;
        branch_id?: string | null;
        rationale?: string | null;
        summary?: string | null;
      } = {};
      if (input.topic !== undefined) {
        const trimmed = input.topic.trim();
        if (!trimmed) throw new Error('A topic needs a title.');
        patch.topic = trimmed;
      }
      if (input.pillarId !== undefined) patch.pillar_id = input.pillarId;
      if (input.branchId !== undefined) patch.branch_id = input.branchId;
      if (input.audience !== undefined) patch.rationale = input.audience?.trim() || null;
      if (input.reference !== undefined) patch.summary = input.reference?.trim() || null;

      if (Object.keys(patch).length === 0) {
        // Nothing to do is not an error, but it must not report a change either.
        return { taskId: input.taskId, wasSent: false };
      }

      const { error: updateError } = await supabase
        .from('project_tasks')
        .update(patch)
        .eq('id', input.taskId)
        .eq('user_id', ownerId);
      if (updateError) throw new Error(updateError.message);

      /*
       * Has a client already been shown this? Only batches that actually went
       * out count — a `draft` request has been shown to nobody, so editing a
       * topic on one is just editing, not a divergence.
       */
      const { data: sentItems } = await supabase
        .from('approval_items')
        .select('id, approval_requests!inner(status)')
        .eq('project_task_id', input.taskId)
        .eq('workspace_id', workspaceId)
        .neq('approval_requests.status', 'draft')
        .limit(1);

      return { taskId: input.taskId, wasSent: (sentItems ?? []).length > 0 };
    },
    onSuccess: () => {
      // Everything that renders a topic: the editorial map, the desk payload
      // both doors read, and the article trail.
      void queryClient.invalidateQueries({ queryKey: ['editorial-map', workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ['desk'] });
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
    },
  });
}
